import { AsyncHelper } from "/js/tools/async.helper.js";
const asyncHelper = AsyncHelper();

const VXConnector = () => {
  const ortusApi = window.ortus.api;

  /**
   * @method getForm
   * @async
   * @param {string} tariffId - The ID of the tariff.
   * @returns {Promise<Object>} The status and data of the form.
   */
  const getForm = async (tariffId) => {
    const url = `${ortusApi}signupform?tariffId=${tariffId}`;
    try {
      const { status, data } = await asyncHelper.getUrl(url);
      return { status, data };
    } catch (e) {
      console.log(e);
    }
  };

  /**
   * @method getTariffData
   * @async
   * @param {string} tariffId - The ID of the tariff.
   * @param {string} energyType - The type of energy.
   * @param {string} query - The query string.
   * @returns {Promise<Object>} The status and data of the tariff.
   */
  const getTariffData = async (tariffId, energyType, query) => {
    const detailUrl = `https://www.verivox.de/servicehook/offers/${energyType}/info/${query}/${tariffId}?partnerId=2624`;

    const proxyUrl = `${ortusApi}package/detail`;

    const { status, data } = await asyncHelper.postUrl(proxyUrl, {
      packageDetailsUri: detailUrl,
    });
    return { status, data };
  };

  /**
   * @method getStreetsForPostCode
   * @async
   * @param {string} postCode - The postal code.
   * @param {string} city - The city name.
   * @returns {Promise<Array<string>>} A list of street names.
   */
  const getStreetsForPostCode = async (postCode, city) => {
    city = decodeURIComponent(city).split(' / ')[0].replace(/\//g, '-').trim();
    let url = `${ortusApi}signupform/streets?postCode=${postCode}&cityName=${city}`;
    const data = await fetch(url).then((response) => response.json());
    const streets = data.map((s) => s.Name);
    return streets;
  };

  /**
   * @method getCityDistricts
   * @async
   * @param {string} postCode - The postal code.
   * @param {string} [energyType='electricity'] - The type of energy.
   * @returns {Promise<Object>} The status and data of the city parts.
   */

  // TODO: use alternative endpoint for getting cities without specifying a producttype
  const getCityDistricts = async (postCode, energyType = 'electricity') => {
    const url = `${ortusApi}location/${postCode}/${energyType}`;
    const { status, data } = await AsyncHelper().getUrl(url);
    return { status, data };
  };

  /**
   * @method getIsValidIBAN
   * @async
   * @param {string} iban - The IBAN to validate.
   * @returns {Promise<Object>} The status and data of the IBAN validation.
   */
  const getIsValidIBAN = async (iban) => {
    const url = `${ortusApi}signupform/iban?iBan=${iban}`;
    // Test for success: DE91500105174885792721
    // Test to fail: GT47195126765214373483528657

    return await asyncHelper.getUrl(url);
  };

  /**
   * @method getBenchmark
   * @async
   * @param {string} [product='Electricity'] - The product type.
   * @param {string} [profile='h0'] - The profile type.
   * @param {string} [postCode='20095'] - The postal code.
   * @param {string} [locationId='23232'] - The location ID.
   * @param {number} [totalUsage=2500] - The total usage.
   * @param {number} [heatingPower=0] - The heating power.
   * @returns {Promise<Object>} The status and data of the benchmark.
   */
  const getBenchmark = async (
    product = "Electricity",
    profile = "h0",
    postCode = "20095",
    locationId = "23232",
    totalUsage = 2500,
    heatingPower = 0
  ) => {
    const url = `${ortusApi}benchmarks?product=${product}&profile=${profile}&postCode=${postCode}&locationId=${locationId}&totalUsage=${totalUsage}&heatingPower=${heatingPower}`;
    return await asyncHelper.getUrl(url);
  };

  /**
   * @method getBenchmarkSuppliers
   * @async
   * @param {string} [product='Electricity'] - The product type.
   * @param {string} [profile='h0'] - The profile type.
   * @param {string} [postCode='20095'] - The postal code.
   * @param {string} [locationId='23232'] - The location ID.
   * @param {number} [totalUsage=2500] - The total usage.
   * @param {number} [heatingPower=0] - The heating power.
   * @returns {Promise<Array<Object>>} A list of suppliers.
   */
  const getBenchmarkSuppliers = async (
    product = "Electricity",
    profile = "h0",
    postCode = "20095",
    locationId = "23232",
    totalUsage = 2500,
    heatingPower = 0
  ) => {
    const { data, status } = await getBenchmark(
      product,
      profile,
      postCode,
      locationId,
      totalUsage,
      heatingPower
    );
    if (status !== 200) {
      throw new Error("Could not fetch suppliers");
    }
    const providers = data.providers.map((p) => {
      return {
        name: p.content.text,
        label: p.content.text,
        id: p.id,
        tariffs: p.products,
      };
    });
    return providers;
  };

/**
 * @method getSuppliers
 * @async
 * @param {string} productType - electricity or gas 
 * @returns {Promise<Array<Object>>}  The status and data of the list of suppliers
 */
  const getSuppliers = async (
    productType = 'Electricity'
  ) => {
    const url = `${ortusApi}providers/${productType}`;

    const apiResult = await asyncHelper.getUrl(url);

    const providers = apiResult.data.map((p) => {
      return {
        name: p.name,
        label: p.name,
        id: p.id,
      };
    });

    return providers;
  }

  /**
   * @method storeData
   * @async
   * @param {Object} postData - The data to post.
   * @param {string} productType - The type of product.
   * @param {string} tariffId - The ID of the tariff.
   * @returns {Promise<Object>} The status and data of the stored data.
   */
  const storeData = async (postData, productType, tariffId) => {
    postData.connectionData.currentProvider = postData.connectionData.currentProvider?.name ?? postData.connectionData.currentProvider;

    if (postData.connectionData.currentProvider === "Andere")
      postData.connectionData.currentProvider = postData.connectionData.otherProvider;

    const url = `${ortusApi}signuprequest/${productType}/${tariffId}`;
    const { status, data } = await asyncHelper.postUrl(url, postData);
    return { status, data };
  };

  /**
   * @method previewPdf
   * @async
   * @param {Object} postData - The data to post.
   * @param {string} productType - The type of product.
   * @param {string} tariffId - The ID of the tariff.
   * @returns {Promise<Object>} The status and data of the PDF preview.
   */
  const previewPdf = async (postData, productType, tariffId) => {
    const url = `${ortusApi}contractPreview/${tariffId}`;
    const { data, status } = await asyncHelper.postUrl(url, postData);
    return { status, data };
  };
  return {
    getCityDistricts,
    getBenchmarkSuppliers,
    getSuppliers,
    getStreetsForPostCode,
    getBenchmark,
    getForm,
    getTariffData,
    getIsValidIBAN,
    storeData,
    previewPdf,
  };
};
export { VXConnector };
